<template>
  <div class="pass-info">
    <div v-show="!isLoading" class="pass-info__body">
      <p data-test="pass-name" class="pass-info__name">{{ membershipPass.attributes.name }}</p>
      <PassBenefits :membership-pass="membershipPass" />
      <div v-if="canEdit" class="pass-info__foot">
        <RecordButton data-test="refund-pass" size="sm" @click="openRefundModal">Refund pass</RecordButton>
        <RecordButton data-test="cancel-pass" variant="danger" size="sm" @click="showCancelModal = true">
          Cancel pass
        </RecordButton>
      </div>
    </div>
    <div v-show="isLoading" class="flex justify-center py-6">
      <BasicSpinner color="gray-900" />
    </div>

    <ConfirmModal
      :is-open="showCancelModal"
      :title="`Cancel the ${membershipPass.attributes.name}?`"
      :message="`${customerName} loses the pass benefits and won't be charged for it again. Past payments aren't refunded.`"
      confirm-label="Cancel pass"
      cancel-label="Keep pass"
      @confirm="handleCancelConfirm"
      @close="showCancelModal = false"
    />

    <TheModal
      v-model:is-open="showRefundModal"
      :modal-title="`Refund the ${membershipPass.attributes.name} for ${customerName}`"
    >
      <template #main>
        <p class="text-sm text-gray-600">Payments {{ customerName }} have made towards this pass.</p>
        <div class="mt-4">
          <MultiSelectInput
            v-model="selectedPayments"
            :options="paymentOptions"
            placeholder="Select payments to refund"
            :show-select-all="paymentOptions.length > 1"
          />
        </div>
      </template>
      <template #footer>
        <button class="btn-ghost justify-center" @click="showRefundModal = false">Cancel</button>
        <button
          class="btn-primary justify-center disabled:opacity-50 disabled:cursor-not-allowed"
          :disabled="!selectedPayments.length"
          @click="handleRefundConfirm"
        >
          Refund
        </button>
      </template>
    </TheModal>
  </div>
</template>

<script>
import { computed, defineComponent, ref } from 'vue'

import BasicSpinner from '@/components/BasicSpinner.vue'
import ConfirmModal from '@/components/ConfirmModal.vue'
import PassBenefits from '@/components/customers/PassBenefits.vue'
import RecordButton from '@/components/customers/record/RecordButton.vue'
import MultiSelectInput from '@/components/neue/MultiSelectInput.vue'
import TheModal from '@/components/TheModal.vue'
import api from '@/store/api'
import { mapType } from '@/store/mappers'

export default defineComponent({
  name: 'PassInfoCard',
  components: {
    BasicSpinner,
    ConfirmModal,
    TheModal,
    MultiSelectInput,
    PassBenefits,
    RecordButton
  },
  props: {
    membershipTier: {
      type: Object,
      required: true
    },
    membershipPass: {
      type: Object,
      required: true
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    customerName: {
      type: String,
      required: true
    },
    conversationUserPairId: {
      type: String,
      required: true
    },
    canEdit: {
      type: Boolean,
      default: false
    }
  },
  emits: ['cancel-pass', 'refund-pass'],
  setup(props, { emit }) {
    const showCancelModal = ref(false)
    const showRefundModal = computed({
      get: () => refundModalOpen.value,
      set: val => {
        refundModalOpen.value = val
        if (!val) {
          selectedPayments.value = []
        }
      }
    })

    const selectedPayments = ref([])

    const paymentTransactions = computed(() => mapType('membership_pass_payment_transaction'))

    const getPaymentTransactions = () => {
      api.call('GET', `conversation_user_pairs/${props.conversationUserPairId}/membership_passes/payment_transactions`)
    }

    const paymentOptions = computed(() => {
      return paymentTransactions.value.map(transaction => ({
        name: `${transaction.attributes.transaction_date} - ${transaction.attributes.amount.format}${
          transaction.attributes.is_refunded ? ' (Refunded)' : ''
        }`,
        value: transaction.id,
        disabled: transaction.attributes.is_refunded
      }))
    })

    const handleCancelConfirm = () => {
      emit('cancel-pass', props.membershipPass.id)
      showCancelModal.value = false
    }

    const handleRefundConfirm = () => {
      selectedPayments.value.forEach(paymentId => {
        api.call(
          'POST',
          `conversation_user_pairs/${props.conversationUserPairId}/membership_passes/payment_transactions/${paymentId}/refund`,
          {
            membership_pass_selection: {
              membership_pass_id: props.membershipPass.id
            }
          }
        )
      })
      showRefundModal.value = false
    }

    const refundModalOpen = ref(false)

    const openRefundModal = () => {
      getPaymentTransactions()
      refundModalOpen.value = true
    }

    return {
      showCancelModal,
      showRefundModal,
      handleCancelConfirm,
      handleRefundConfirm,
      selectedPayments,
      paymentOptions,
      openRefundModal
    }
  }
})
</script>

<style scoped>
.pass-info,
.pass-info__body {
  display: flex;
  flex: 1;
  flex-direction: column;
}
.pass-info__name {
  margin: 0 0 10px;
  font-size: 13.5px;
  font-weight: 600;
  color: #15181d;
}
.pass-info__foot {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: auto;
  padding-top: 14px;
}
</style>
